interface BandSpec {
  key: 'critical' | 'weak' | 'solid' | 'promising' | 'validated'
  label: string
  min: number
  segClass: string
  color: string
  fillColor: string
}

const BANDS: BandSpec[] = [
  {
    key: 'critical',
    label: 'Critical',
    min: 0,
    segClass: 'bg-danger-bg text-danger-fg',
    color: '#c8554b',
    fillColor: 'rgba(200, 85, 75, 0.14)',
  },
  {
    key: 'weak',
    label: 'Weak',
    min: 20,
    segClass: 'bg-[#fbe4d4] text-[#8a4514]',
    color: '#c97a3a',
    fillColor: 'rgba(201, 122, 58, 0.13)',
  },
  {
    key: 'solid',
    label: 'Solid',
    min: 40,
    segClass: 'bg-warning-bg text-warning-fg',
    color: '#c69234',
    fillColor: 'rgba(198, 146, 52, 0.13)',
  },
  {
    key: 'promising',
    label: 'Promising',
    min: 60,
    segClass: 'bg-[#e3f0e8] text-[#2c6a3f]',
    color: '#5d9168',
    fillColor: 'rgba(93, 145, 104, 0.14)',
  },
  {
    key: 'validated',
    label: 'Validated',
    min: 80,
    segClass: 'bg-success-bg text-success-fg',
    color: '#3e8553',
    fillColor: 'rgba(62, 133, 83, 0.15)',
  },
]

export function classifyBand(score: number): BandSpec {
  let active = BANDS[0]
  for (const band of BANDS) {
    if (score >= band.min) active = band
  }
  return active
}

interface ValidationScoreBandProps {
  score: number
}

export function ValidationScoreBand({ score }: ValidationScoreBandProps) {
  const clamped = Math.max(0, Math.min(100, score))
  const active = classifyBand(clamped)

  return (
    <section className="mt-5">
      <div className="relative pt-7">
        <div
          className="absolute top-0 -translate-x-1/2 flex flex-col items-center gap-1"
          style={{ left: `${clamped}%` }}
        >
          <div
            className="px-2 py-0.5 rounded-full text-fg-inverse font-mono tabular-nums whitespace-nowrap"
            style={{
              background: 'var(--color-fg)',
              fontSize: 10.5,
              letterSpacing: '0.04em',
            }}
          >
            {clamped}%  ·  {active.label}
          </div>
          <div className="w-[1.5px] h-1.5" style={{ background: 'var(--color-fg)' }} />
        </div>

        <div className="grid grid-cols-5 gap-1">
          {BANDS.map((band) => {
            const isActive = band.key === active.key
            return (
              <div
                key={band.key}
                className={`text-center rounded-full font-mono uppercase font-medium transition-opacity duration-[var(--dur-normal)] ${band.segClass} ${
                  isActive ? 'opacity-100' : 'opacity-40'
                }`}
                style={{
                  padding: '6px 8px',
                  fontSize: 10.5,
                  letterSpacing: '0.06em',
                }}
              >
                {band.label}
              </div>
            )
          })}
        </div>
      </div>
      <div
        className="mt-2.5 flex justify-between text-fg-muted font-mono"
        style={{ fontSize: 11, letterSpacing: '0.04em' }}
      >
        <span>Overall confidence (avg across {BANDS.length} stages)</span>
        <span>Validated band starts at 80%</span>
      </div>
    </section>
  )
}
